import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "@/lib/api";
import {
  GridFour,
  CircleNotch,
  Sparkle,
  Key,
  WarningCircle,
  CheckCircle,
  DownloadSimple,
  Trash,
  ArrowSquareOut,
} from "@phosphor-icons/react";

/**
 * Carousel Studio — generates Instagram/LinkedIn carousels via PostNitro.
 *
 * 3-state UI:
 *  1. No API key on file → "Add Embed API Key" form
 *  2. Has key but no brands/templates → "Go set up your PostNitro account" CTA
 *  3. Fully configured → Generator form + job history
 */
const SLIDE_COUNTS = [3, 5, 7, 9, 12];
const TONES = [
  { value: "gossip-wisdom", label: "Gossip Wisdom" },
  { value: "lux-confessional", label: "Lux Confessional" },
  { value: "no-bs", label: "No BS" },
  { value: "soft-life", label: "Soft Life" },
  { value: "educational", label: "Educational" },
  { value: "story", label: "Story" },
];

export default function CarouselStudio() {
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyInput, setKeyInput] = useState("");
  const [keySaving, setKeySaving] = useState(false);
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("gossip-wisdom");
  const [slideCount, setSlideCount] = useState(7);
  const [brandId, setBrandId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);

  const fetchSetup = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/carousel/setup`);
      setSetup(res.data);
      if (res.data.default_brand_id) setBrandId(res.data.default_brand_id);
      if (res.data.default_template_id) setTemplateId(res.data.default_template_id);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/carousel/jobs`);
      setJobs(res.data.jobs || []);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => { fetchSetup(); fetchJobs(); }, [fetchSetup, fetchJobs]);

  // Poll jobs every 5s while any job is running
  useEffect(() => {
    const anyActive = jobs.some(j => ["starting", "scripting", "rendering"].includes(j.status));
    if (!anyActive) return;
    const t = setInterval(fetchJobs, 5000);
    return () => clearInterval(t);
  }, [jobs, fetchJobs]);

  const saveKey = async () => {
    if (!keyInput.trim()) return;
    setKeySaving(true);
    setError(null);
    try {
      await axios.post(`${API}/carousel/save-key`, { api_key: keyInput.trim() });
      setKeyInput("");
      await fetchSetup();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save API key");
    } finally {
      setKeySaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!topic.trim() || !brandId || !templateId) return;
    setGenerating(true);
    setError(null);
    try {
      await axios.post(`${API}/carousel/generate`, {
        topic: topic.trim(),
        tone,
        slide_count: slideCount,
        brand_id: brandId,
        template_id: templateId,
      });
      setTopic("");
      await fetchJobs();
    } catch (e) {
      setError(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const saveDefaults = async () => {
    try {
      await axios.post(`${API}/carousel/set-defaults`, { brand_id: brandId, template_id: templateId });
    } catch (_) { /* silent */ }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm("Delete this carousel job?")) return;
    try {
      await axios.delete(`${API}/carousel/jobs/${jobId}`);
      fetchJobs();
    } catch (_) { /* silent */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch size={32} className="animate-spin text-[#E91E8C]" />
      </div>
    );
  }

  // STATE 1 — No API key on file
  if (!setup?.has_key) {
    return (
      <div className="space-y-5" data-testid="carousel-studio-page">
        <Header />
        <div className="card p-6 max-w-2xl" data-testid="carousel-add-key">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#E91E8C]/10 flex items-center justify-center flex-shrink-0">
              <Key size={20} className="text-[#E91E8C]" weight="duotone" />
            </div>
            <div>
              <h3 className="text-base font-bold text-zinc-900">Connect your PostNitro account</h3>
              <p className="text-sm text-zinc-500 mt-0.5">Carousel Studio uses PostNitro to render visually beautiful 9:16 / square slides from your scripts.</p>
            </div>
          </div>
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">Embed API Key</label>
          <input
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            type="password"
            placeholder="pn-..."
            className="input-field text-sm font-mono"
            data-testid="carousel-key-input"
          />
          <p className="text-xs text-zinc-400 mt-2">
            Get yours at <a href="https://postnitro.ai/docs/embed/obtaining-an-api-key" target="_blank" rel="noopener noreferrer" className="text-[#E91E8C] hover:underline">postnitro.ai → Account → Embed API Key</a>. Free plan = 5 credits/month, $10 plan = 250 credits.
          </p>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          <button
            onClick={saveKey}
            disabled={!keyInput.trim() || keySaving}
            className="mt-3 px-4 py-2 bg-[#E91E8C] text-white rounded-xl font-semibold text-sm hover:bg-[#d4177d] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="carousel-save-key-btn"
          >
            {keySaving ? "Validating..." : "Connect PostNitro"}
          </button>
        </div>
      </div>
    );
  }

  // STATE 2 — Has key but no brands/templates set up
  if (setup.brands_empty || setup.templates_empty) {
    return (
      <div className="space-y-5" data-testid="carousel-studio-page">
        <Header />
        <div className="card p-6 max-w-2xl border-amber-200 bg-amber-50/60" data-testid="carousel-no-brands">
          <div className="flex items-start gap-3">
            <WarningCircle size={20} weight="fill" className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-base font-bold text-amber-900">Your PostNitro account is empty</h3>
              <p className="text-sm text-amber-800 mt-1">You need at least <b>1 Brand</b> and <b>1 Template</b> in your PostNitro dashboard before carousels can be generated. Takes ~5 min.</p>
              <a
                href="https://postnitro.ai/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-amber-900 hover:text-amber-700"
              >
                Open PostNitro dashboard <ArrowSquareOut size={14} />
              </a>
              <p className="text-xs text-amber-700 mt-2">Once you've created them, click the refresh button below.</p>
              <button
                onClick={() => { setLoading(true); fetchSetup(); }}
                className="mt-3 px-3 py-1.5 text-xs bg-amber-700 text-white rounded-lg font-semibold hover:bg-amber-800"
                data-testid="carousel-refresh-setup"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // STATE 3 — Fully set up
  return (
    <div className="space-y-5" data-testid="carousel-studio-page">
      <Header />

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Generator */}
        <div className="card p-5 space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500">Create a carousel</h3>

          <div>
            <label className="text-xs text-zinc-500 font-medium block mb-1">Topic</label>
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. 5 mistakes new beauty creators make"
              className="input-field text-sm"
              data-testid="carousel-topic-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 font-medium block mb-1">Tone</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} className="input-field text-sm" data-testid="carousel-tone">
                {TONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500 font-medium block mb-1">Slides</label>
              <select value={slideCount} onChange={(e) => setSlideCount(parseInt(e.target.value))} className="input-field text-sm" data-testid="carousel-slides">
                {SLIDE_COUNTS.map(n => <option key={n} value={n}>{n} slides</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500 font-medium block mb-1">Brand</label>
              <select value={brandId} onChange={(e) => { setBrandId(e.target.value); }} className="input-field text-sm" data-testid="carousel-brand">
                {(setup.brands || []).map(b => (
                  <option key={b.id || b._id} value={b.id || b._id}>{b.name || b.brandName || "Brand"}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500 font-medium block mb-1">Template</label>
              <select value={templateId} onChange={(e) => { setTemplateId(e.target.value); }} className="input-field text-sm" data-testid="carousel-template">
                {(setup.templates || []).map(t => (
                  <option key={t.id || t._id} value={t.id || t._id}>{t.name || t.templateName || "Template"}</option>
                ))}
              </select>
            </div>
          </div>

          <button onClick={saveDefaults} className="text-xs text-zinc-400 hover:text-zinc-600 underline" data-testid="carousel-save-defaults">
            Save as my defaults
          </button>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            onClick={handleGenerate}
            disabled={!topic.trim() || !brandId || !templateId || generating}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#E91E8C] text-white rounded-xl font-bold text-sm hover:bg-[#d4177d] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="carousel-generate-btn"
          >
            {generating ? <><CircleNotch size={16} className="animate-spin" /> Queuing…</> : <><Sparkle size={16} weight="fill" /> Generate carousel</>}
          </button>

          <p className="text-[11px] text-zinc-400 leading-relaxed">
            Slides are written by Claude using your Brand KB so the voice is on-brand. Cost: ~{slideCount} PostNitro credits ({(slideCount * 0.04).toFixed(2)} USD on the $10 plan) + a few cents in Claude tokens.
          </p>
        </div>

        {/* Jobs / Output */}
        <div className="space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500">Recent carousels</h3>
          {jobs.length === 0 ? (
            <div className="card p-6 text-center text-sm text-zinc-400" data-testid="carousel-no-jobs">
              No carousels yet — generate your first one ←
            </div>
          ) : (
            <div className="space-y-3" data-testid="carousel-jobs-list">
              {jobs.map(j => <JobCard key={j.id} job={j} onDelete={() => deleteJob(j.id)} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-[#E91E8C]/10 flex items-center justify-center">
        <GridFour size={20} weight="duotone" className="text-[#E91E8C]" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">Carousel Studio</h2>
        <p className="text-zinc-500 text-sm">Brand-on-voice Instagram & LinkedIn carousels rendered via PostNitro.</p>
      </div>
    </div>
  );
}

function JobCard({ job, onDelete }) {
  const isProcessing = ["starting", "scripting", "rendering"].includes(job.status);
  const isFailed = job.status === "failed";
  const isDone = job.status === "completed";
  const slides = job.slides_output || [];

  return (
    <div className="card p-4 space-y-3" data-testid={`carousel-job-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-900 truncate">{job.topic}</p>
          <p className="text-[11px] text-zinc-400 mt-0.5">{job.slide_count} slides · {job.tone}</p>
        </div>
        <button onClick={onDelete} className="text-zinc-300 hover:text-red-500" data-testid={`carousel-delete-${job.id}`}>
          <Trash size={14} />
        </button>
      </div>

      {isProcessing && (
        <div className="flex items-center gap-2 bg-zinc-50 rounded-lg p-2 text-xs text-zinc-600">
          <CircleNotch size={14} className="animate-spin" />
          {job.current_step === "writing-slides" ? "Claude is writing your slide content..."
            : job.current_step === "initiating" ? "Sending to PostNitro..."
            : "Rendering slides (typically 20-60s)..."}
        </div>
      )}

      {isFailed && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
          <WarningCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span>{job.error || "Generation failed"}</span>
        </div>
      )}

      {isDone && slides.length > 0 && (
        <>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="carousel-result-slides">
            {slides.map((s, i) => (
              <a
                key={i}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 w-24 aspect-square rounded-lg overflow-hidden bg-zinc-100 border border-zinc-200 hover:border-[#E91E8C] transition-colors relative group"
              >
                <img src={s.url} alt={`Slide ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-black/60 text-white text-[10px] font-bold rounded">{i + 1}</div>
              </a>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
              <CheckCircle size={11} weight="fill" /> Saved to Vault
            </span>
            {job.pdf_url && (
              <a href={job.pdf_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[11px] bg-zinc-50 text-zinc-700 hover:bg-zinc-100 px-2 py-0.5 rounded-full font-medium">
                <DownloadSimple size={11} /> PDF
              </a>
            )}
            {slides.map((s, i) => (
              <a key={i} href={s.url} download={`carousel-slide-${i + 1}.png`} className="text-[10px] text-zinc-400 hover:text-[#E91E8C]" data-testid={`carousel-slide-dl-${i}`}>
                ⬇ {i + 1}
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
